import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Copy, RefreshCw, Dices } from 'lucide-react';
import { toast } from 'sonner';

const RandomNumberTool = () => {
  const [min, setMin] = useState(1);
  const [max, setMax] = useState(100);
  const [count, setCount] = useState(1);
  const [results, setResults] = useState([]);
  const [allowDuplicates, setAllowDuplicates] = useState(true);

  const generate = () => {
    const numbers = [];
    const range = max - min + 1;
    
    if (!allowDuplicates && count > range) {
      toast.error(`Cannot generate ${count} unique numbers in range ${min}-${max}`);
      return;
    }

    while (numbers.length < count) {
      const num = Math.floor(Math.random() * range) + min;
      if (allowDuplicates || !numbers.includes(num)) {
        numbers.push(num);
      }
    }

    setResults(numbers);
  };

  const copyResults = () => {
    navigator.clipboard.writeText(results.join(', '));
    toast.success('Numbers copied!');
  };

  return (
    <div className="space-y-6" data-testid="random-number-tool">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Minimum</label>
          <Input
            type="number"
            value={min}
            onChange={(e) => setMin(parseInt(e.target.value) || 0)}
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Maximum</label>
          <Input
            type="number"
            value={max}
            onChange={(e) => setMax(parseInt(e.target.value) || 0)}
          />
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Count</label>
          <Input
            type="number"
            min={1}
            max={100}
            value={count}
            onChange={(e) => setCount(Math.min(100, parseInt(e.target.value) || 1))}
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 h-10">
            <input
              type="checkbox"
              checked={allowDuplicates}
              onChange={(e) => setAllowDuplicates(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Allow Duplicates</span>
          </label>
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={generate} className="glow-blue">
          <Dices className="w-4 h-4 mr-2" /> Generate
        </Button>
        <Button onClick={copyResults} variant="outline" disabled={results.length === 0}>
          <Copy className="w-4 h-4 mr-2" /> Copy
        </Button>
        <Button onClick={() => setResults([])} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear
        </Button>
      </div>

      {results.length > 0 && (
        <div className="p-6 rounded-xl bg-muted/30 border border-border">
          <h3 className="font-semibold mb-4">Results ({results.length} number{results.length > 1 ? 's' : ''})</h3>
          <div className="flex flex-wrap gap-3">
            {results.map((num, i) => (
              <span 
                key={i} 
                className="px-4 py-2 rounded-lg bg-primary/10 text-primary font-mono text-lg font-semibold"
              >
                {num}
              </span>
            ))}
          </div>
          {results.length > 1 && (
            <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Sum:</span>
                <span className="ml-2 font-mono">{results.reduce((a, b) => a + b, 0)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Average:</span>
                <span className="ml-2 font-mono">{(results.reduce((a, b) => a + b, 0) / results.length).toFixed(2)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Range:</span>
                <span className="ml-2 font-mono">{Math.min(...results)} - {Math.max(...results)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RandomNumberTool;
